import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client/react';
import { Trash2, Loader2, CirclePause, Monitor, AlertTriangle } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@scio3/ui';
import { isEnterKey } from '@/lib/keyboard';
import {
  UNPAIR_DEVICES,
  MOVE_TO_GROUP,
  GET_SCREEN_MANAGEMENT_DEVICES_QUERY,
} from '@scio3/graphql-client';
import { getUnsupportedFreePlanReason } from '../utils/billing-constants';
import { ON_HOLD_DEVICE_FOLDER } from '@scio3/shared-types';
import { toast } from 'sonner';
import { useConfirm } from '@/components/shared';
import { useTeamStore } from '@/stores/team-store';
import { usePermissions } from '@/hooks/usePermissions';
// #1244 — FREE_PLAN_MAX_DEVICES + isUnsupportedFreePlanDevice no longer needed
// here; backend applies all team-scope + unsupported filtering in
// getScreenManagementDevices and returns the rendered list directly.

const ONHOLD_PATH = `/${ON_HOLD_DEVICE_FOLDER}`;

type ScreenManagementDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Current total number of paired screens */
  totalPairedScreens: number;
  subAccountId?: string;
  teamId?: string;
  /**
   * SP53 — i18n key for the dialog's body message. Defaults to `screenManagementModal1`
   * (decrease-screen-count flow). Set to `screenManagementModalFreePlan` when opened
   * from a Free-plan downgrade error so the user sees the Free-plan-specific guidance.
   */
  messageKey?: string;
  /**
   * Optional callback fired when the user clicks "Continue" after resolving
   * all blocking devices (i.e. the device list is empty). Parents that opened
   * this dialog as part of a multi-step flow (e.g. paid → Free downgrade
   * blocked by LIMIT_QUANTITY) can pass a callback here to auto-retry the
   * original action so the user doesn't have to click their way back. When
   * omitted, Continue just closes the dialog like Cancel.
   */
  onContinue?: () => void;
};

interface DeviceNode {
  _id: string;
  deviceName?: string;
  path?: string;
  // #1244 — teamId required so the unpair / move-to-OnHold mutations can
  // target the device's own team (devices may live in different member
  // teams that share billing with the account).
  teamId?: string;
  // #1140 — device-type fields kept for row display; the unsupported
  // predicate now runs server-side in getFreePlanDowngradeDevices.
  os?: string;
  device?: string;
  model?: string;
  typeBuild?: string;
  manufacturer?: string;
}

// #1244 — Single response shape covering both Screen Management flows
// (decrease-count + Free-plan downgrade), team scope (single / cross-team).
// Backend: company.resolver.ts → getScreenManagementDevices.
interface ScreenManagementResponse {
  getScreenManagementDevices: {
    status: boolean;
    totalScreens?: number;
    numberDeviceNotSupport?: number;
    numberDeviceBYOD?: number;
    numberTeam?: number;
    devices?: DeviceNode[];
  };
}

/**
 * Screen Management dialog matching scio1's screenManagementModal.
 * Shows when user tries to decrease subscription screen count.
 * Lists paired devices with delete (unpair) buttons.
 */
export function ScreenManagementDialog({
  open,
  onClose,
  totalPairedScreens,
  subAccountId,
  teamId,
  messageKey = 'screenManagementModal1',
  onContinue,
}: ScreenManagementDialogProps) {
  const { t } = useTranslation();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  // QC #1319 — Team Owner / Team Admin are legitimate managers of their
  // team's billing subscription (scio1 parity). When the dialog is opened
  // in team-billing context (teamId set), widen the gate to include
  // isTeamAdmin. Root context still requires isRootAdmin. Backend already
  // accepts the Team Owner role for team-scoped operations.
  const { isRootAdmin, isTeamAdmin } = usePermissions();
  const canManage = isRootAdmin || (!!teamId && isTeamAdmin);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  // Type-DELETE-to-confirm unpair flow (matches RemoveScreenDialog pattern + scio1 parity).
  const [deviceToUnpair, setDeviceToUnpair] = useState<DeviceNode | null>(null);
  const [unpairConfirmText, setUnpairConfirmText] = useState('');
  const [unpairError, setUnpairError] = useState('');
  const confirm = useConfirm();

  const isFreePlanMode = messageKey === 'screenManagementModalFreePlan';

  // #1244 — Single backend query for both Screen Management flows.
  // Server handles team scope (single via teamId, or cross-team if omitted)
  // AND the scio1 Free-plan two-tier rule. Frontend just renders.
  //
  // Role gate: only Owner / root Admin / Super Admin can use this popup
  // (the cross-team device list is company-confidential). Backend
  // (`company.resolver.ts:getScreenManagementDevices`) is the source of
  // truth — this FE skip + early-return below is purely UX to avoid a
  // wasted query and a flicker between "open" and the backend rejection.
  const { data, loading, refetch } = useQuery<ScreenManagementResponse>(
    GET_SCREEN_MANAGEMENT_DEVICES_QUERY,
    {
      variables: {
        subAccountId,
        teamId,
        freePlanDowngrade: isFreePlanMode,
      },
      skip: !open || !canManage,
      fetchPolicy: 'network-only',
    },
  );

  const [unpairDevices] = useMutation(UNPAIR_DEVICES, {
    // Keep 'Devices' for backward-compatible refresh of any concurrently
    // mounted DEVICES_QUERY consumer (e.g. Screens page). 'GetScreenManagementDevices'
    // refreshes this dialog's own list after the mutation.
    refetchQueries: ['Devices', 'GetTotalScreensCount', 'GetScreenManagementDevices'],
  });

  // SP53 — move device to OnHold Devices folder (does not consume a license).
  const [moveToGroup] = useMutation(MOVE_TO_GROUP, {
    refetchQueries: ['Devices', 'GetTotalScreensCount', 'GetScreenManagementDevices'],
  });

  const devices: DeviceNode[] = data?.getScreenManagementDevices?.devices ?? [];
  const totalCount =
    data?.getScreenManagementDevices?.totalScreens ?? (totalPairedScreens || devices.length);

  // Open the type-DELETE-to-confirm dialog. Actual mutation runs in confirmUnpair.
  const handleDelete = useCallback((device: DeviceNode) => {
    setDeviceToUnpair(device);
    setUnpairConfirmText('');
    setUnpairError('');
  }, []);

  const closeUnpairDialog = useCallback(() => {
    setDeviceToUnpair(null);
    setUnpairConfirmText('');
    setUnpairError('');
  }, []);

  const confirmUnpair = useCallback(async () => {
    if (!deviceToUnpair) return;
    if (unpairConfirmText.trim().toUpperCase() !== 'DELETE') {
      setUnpairError(t('Please type "DELETE" and click OK to continue.'));
      return; // (button is disabled in this state — defensive check only)
    }
    const device = deviceToUnpair;
    setRemovingId(device._id);
    try {
      await unpairDevices({
        variables: {
          // #1244 — Free-plan mode lists devices across all teams that share
          // billing; the unpair mutation must target the device's own team,
          // not the dialog's active team, otherwise cross-team rows fail.
          teamId: device.teamId ?? teamId,
          payload: { deviceIds: [device._id] },
        },
      });
      toast.success(t('Device unpaired successfully'));
      refetch();
      closeUnpairDialog();
    } catch {
      toast.error(t('Failed to unpair device'));
    } finally {
      setRemovingId(null);
    }
  }, [deviceToUnpair, unpairConfirmText, unpairDevices, teamId, refetch, t, closeUnpairDialog]);

  // SP53 — move a device to OnHold Devices folder (paused, no license consumed).
  const handleMoveToOnHold = useCallback(
    async (device: DeviceNode) => {
      const result = await confirm({
        title: t('Move to OnHold Devices'),
        description: t(
          'Do you want to move this device to the OnHold Devices folder? The screen will display blank content until moved back.',
        ),
        confirm: { label: t('Move to OnHold') },
      });
      if (result !== 'confirm') return;

      setMovingId(device._id);
      try {
        await moveToGroup({
          variables: {
            // #1244 — Free-plan mode lists devices across all teams that share
            // billing; the move-to-OnHold mutation must target the device's
            // own team, not the dialog's active team.
            teamId: device.teamId ?? activeTeamId,
            payload: { type: 'DEVICE', newPath: ONHOLD_PATH, ids: [device._id] },
          },
        });
        toast.success(t('Device moved to OnHold'));
        refetch();
      } catch {
        toast.error(t('Failed to move device'));
      } finally {
        setMovingId(null);
      }
    },
    [moveToGroup, activeTeamId, refetch, t, confirm],
  );

  // #1244 — Permission-denied early return for non-rootAdmin.
  // #1319 — also accept team admins/owners when opened in team-billing
  // context. Backend enforces the same check; this is purely UX to avoid
  // a flicker between "open" → query rejects → empty-list state.
  if (open && !canManage) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-md" data-testid="screen-management-dialog">
          <DialogHeader>
            <DialogTitle>{t('Permission denied')}</DialogTitle>
          </DialogHeader>
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t('Only Owner, Super Admin, or Admin can manage screens for subscription changes.')}
          </div>
          <DialogFooter>
            <Button onClick={onClose} className="cursor-pointer">
              {t('Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
      >
        <DialogContent
          className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-xl"
          data-testid="screen-management-dialog"
        >
          <DialogHeader variant="muted" className="mb-0">
            <DialogTitle className="flex items-center gap-2">
              <Monitor className="h-5 w-5 text-muted-foreground" />
              {t('Screen Management')}
            </DialogTitle>
            <DialogDescription>
              {isFreePlanMode
                ? t('Move or remove devices to fit the Free Plan.')
                : t('Move screens to OnHold or remove them to match your subscription.')}
            </DialogDescription>
          </DialogHeader>

          {/* Warning callout — same pattern as DeleteAccountDialog so the
              constraint (3-screen cap + supported device families) is impossible
              to miss before the user picks a row to unpair or move. */}
          <Alert variant="warning" className="mt-4 mb-6 [&>svg+div]:translate-y-0">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t(messageKey, { screenNumber: String(totalCount) })}
            </AlertDescription>
          </Alert>

          {/* List label sits OUTSIDE the bordered container so the panel feels
              like a labeled section (header label → bordered panel). */}
          <div className="mb-2 text-sm font-medium text-foreground">{t('Device(s)')}</div>

          {/* Device list — wrapped in a bordered container so the list feels
              like its own panel, mirroring MoveToTeamDialog's list styling. */}
          <div className="flex-1 overflow-y-auto rounded-md border px-4">
            {loading && devices.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <table className="w-full">
                <tbody>
                  {devices.map((device) => {
                    const busy = removingId === device._id || movingId === device._id;
                    const isInOnHold = device.path === ONHOLD_PATH;
                    // #1362 — Surface WHY the row is here so the user can tell
                    // an "unsupported device type" (Roku / Firestick / iOS / etc.)
                    // apart from an "over the 3-screen cap" excess row.
                    const unsupportedReason = isFreePlanMode
                      ? getUnsupportedFreePlanReason(device)
                      : null;
                    return (
                      <tr
                        key={device._id}
                        data-testid="screen-management-row"
                        className="border-b border-border last:border-b-0"
                      >
                        <td className="py-3 text-sm">
                          <div className="flex flex-col">
                            <span>
                              {device.path ? `${device.path}/` : ''}
                              {device.deviceName ?? device._id}
                            </span>
                            {unsupportedReason && (
                              <span
                                className="text-xs text-destructive"
                                data-testid="screen-management-row-reason"
                              >
                                {t('{{type}} — not supported on Free', {
                                  type: unsupportedReason,
                                })}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-right">
                          <TooltipProvider delayDuration={300}>
                            <div className="flex items-center justify-end gap-2">
                              {/* Move to OnHold — offered in BOTH flows. Deliberately
                                  diverges from scio1, which gated this on
                                  `isDowngradeFreePlan` and so left the decrease-screen-
                                  count flow with unpair as the only way out, destroying
                                  the pairing for what is usually a temporary reduction.
                                  OnHold frees a license identically on a paid plan: the
                                  move sets `feature.isOnHold` (device.hook.ts
                                  preFindOneAndUpdate) and getScreenManagementDevices
                                  counts with `feature.isOnHold: {$ne: true}`.
                                  Hidden when the device is already in OnHold. Non-admins
                                  never reach this row (dialog early-returns "Permission
                                  denied", matching backend ONHOLD_ADMIN_REQUIRED). */}
                              {!isInOnHold && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      data-testid="screen-management-onhold-btn"
                                      variant="outline"
                                      size="icon-sm"
                                      onClick={() => handleMoveToOnHold(device)}
                                      disabled={busy}
                                      aria-label={t('Move to OnHold')}
                                      className={cn(
                                        'shrink-0',
                                        busy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                                      )}
                                    >
                                      {movingId === device._id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <CirclePause className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>{t('Move to OnHold')}</TooltipContent>
                                </Tooltip>
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    data-testid="screen-management-unpair-btn"
                                    variant="outline"
                                    size="icon-sm"
                                    onClick={() => handleDelete(device)}
                                    disabled={busy}
                                    aria-label={t('Unpair device')}
                                    className={cn(
                                      'shrink-0',
                                      busy
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'cursor-pointer hover:text-destructive',
                                    )}
                                  >
                                    {removingId === device._id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('Unpair device')}</TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        </td>
                      </tr>
                    );
                  })}

                  {devices.length === 0 && !loading && (
                    <tr>
                      <td colSpan={2} className="py-6 text-center text-sm text-muted-foreground">
                        {t('No devices found')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* #1244 — Pagination removed: backend returns a bounded list. */}
          </div>

          {/* Footer varies by mode:
              - Free-plan downgrade: Cancel + "Downgrade to Free" because the
                user is mid-flow — clicking the primary action retries the
                downgrade in-place via the parent's `onContinue` callback.
              - Decrease-screens: single Close. The user is removing screens
                to lower their subscription count; the next step happens back
                in the wizard (where they confirm the lower count), so no
                inline retry is needed and a single dismiss is cleaner. */}
          {isFreePlanMode ? (
            <DialogFooter>
              <Button
                data-testid="screen-management-close-btn"
                variant="outline"
                onClick={onClose}
                className="cursor-pointer"
              >
                {t('Cancel')}
              </Button>
              <Button
                data-testid="screen-management-continue-btn"
                disabled={devices.length > 0}
                disabledTooltip={t(
                  'Remove or move the listed devices to fit the Free plan before continuing.',
                )}
                onClick={() => {
                  onContinue?.();
                  onClose();
                }}
                className="cursor-pointer"
              >
                {t('Downgrade to Free')}
              </Button>
            </DialogFooter>
          ) : (
            <DialogFooter>
              <Button
                data-testid="screen-management-close-btn"
                variant="outline"
                onClick={onClose}
                className="cursor-pointer"
              >
                {t('Close')}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Type-DELETE-to-confirm unpair dialog — matches scio1 parity */}
      <AlertDialog
        open={!!deviceToUnpair}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeUnpairDialog();
        }}
      >
        <AlertDialogContent
          data-testid="unpair-device-confirm-dialog"
          onOpenAutoFocus={(e) => {
            // Radix's default focuses AlertDialogCancel for destructive dialogs,
            // which makes Enter (anywhere in the dialog) trigger Cancel and close
            // the dialog. Override: focus the input so Enter has nowhere to go,
            // and the input's own onKeyDown swallows Enter to keep it inert.
            e.preventDefault();
            // Defer to ensure the input is mounted before we focus it.
            requestAnimationFrame(() => {
              const el = document.querySelector<HTMLInputElement>(
                '[data-testid="unpair-device-confirm-input"]',
              );
              el?.focus();
            });
          }}
          onKeyDown={(e) => {
            // Belt-and-suspenders: even if focus escapes the input, never let Enter
            // fire a button at the dialog level.
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Delete Screen')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="rounded-md bg-warning/10 p-3 text-sm text-foreground">
                {t('This will remove the selected Device(s).')}{' '}
                <strong className="text-[red]">{t('This action cannot be undone.')}</strong>{' '}
                {t('Do you wish to continue?')}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 py-2">
            <p className="text-sm">
              {t('Type')} <span className="font-bold">DELETE</span> {t('to confirm')}:
            </p>
            <Input
              type="text"
              value={unpairConfirmText}
              onChange={(e) => {
                setUnpairConfirmText(e.target.value);
                if (unpairError) setUnpairError('');
              }}
              onKeyDown={(e) => {
                // Swallow Enter entirely — without stopPropagation it bubbles to
                // Radix's AlertDialog which closes the dialog via the focused
                // AlertDialogCancel (the safe default when the destructive button
                // is disabled). User must click OK to confirm.
                if (isEnterKey(e)) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              autoFocus
              data-testid="unpair-device-confirm-input"
            />
            {unpairError && <p className="text-sm text-destructive">{unpairError}</p>}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeUnpairDialog} data-testid="unpair-cancel-btn">
              {t('Cancel')}
            </AlertDialogCancel>
            {/* Manual Tooltip wrap so the disabled-tooltip renders above the
              AlertDialog (z-[70]). The Button's built-in disabledTooltip uses
              the default Radix Tooltip z-50, which renders behind the dialog. */}
            {(() => {
              const isInvalid = unpairConfirmText.trim().toUpperCase() !== 'DELETE';
              const isBusy = removingId !== null;
              const deleteBtn = (
                <Button
                  variant="destructive"
                  onClick={confirmUnpair}
                  disabled={isInvalid || isBusy}
                  data-testid="unpair-confirm-btn"
                >
                  {isBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('Delete')}
                </Button>
              );
              if (!isInvalid) return deleteBtn;
              return (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* span wrapper because disabled buttons don't fire pointer events */}
                      <span className="inline-flex cursor-not-allowed">{deleteBtn}</span>
                    </TooltipTrigger>
                    <TooltipContent sideOffset={4} className="z-[80]">
                      {t('Please type "DELETE" and click OK to continue.')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })()}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
